import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, MapPin, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { events, churchInfo } from '../mockData';

export const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#800020] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">Services & Events</h1>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-[#fef3c7]">
            Join us for worship, fellowship, and spiritual growth
          </p>
        </div>
      </section>

      {/* Regular Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Weekly Services</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-1 gap-8 max-w-3xl mx-auto">
            {/* Sunday Services */}
            <Card className="bg-[#fffbeb] border-2 border-[#800020]">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#800020] mb-6">Sunday Services</h3>
                <div className="bg-[#D4AF37] text-white p-4 rounded-lg mb-6 text-center font-semibold">
                  1st, 3rd, and 4th Sundays of each month
                </div>
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h4 className="text-xl font-bold text-[#D4AF37] mb-2">Sunday School</h4>
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <Clock className="h-4 w-4" />
                      <span>9:30 AM</span>
                    </div>
                    <p className="text-gray-700">
                      Bible study for all ages with age-appropriate classes for children, youth, and adults.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h4 className="text-xl font-bold text-[#D4AF37] mb-2">Church Service</h4>
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <Clock className="h-4 w-4" />
                      <span>10:00 AM</span>
                    </div>
                    <p className="text-gray-700">
                      Our main worship service featuring dynamic preaching, uplifting music, and heartfelt praise.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Church Calendar */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Church Calendar</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">
              Stay connected with what's happening at Mt. Olive
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <Card key={event.id} className="bg-white hover:shadow-xl transition-shadow border-2 border-[#800020]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="bg-[#D4AF37] text-white p-3 rounded-lg">
                      <Calendar className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#800020] mb-1">{event.title}</h3>
                      {event.recurring && (
                        <span className="inline-block bg-[#fffbeb] text-[#D4AF37] text-xs px-2 py-1 rounded border border-[#D4AF37]">
                          Recurring
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4 text-[#D4AF37]" />
                      <span className="text-sm">{event.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4 text-[#D4AF37]" />
                      <span className="text-sm">{event.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" />
                      <span className="text-sm">{event.location}</span>
                    </div>
                  </div>

                  <p className="text-gray-700 text-sm">
                    {event.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Special Events */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Special Events</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-[#fffbeb] border-2 border-[#800020]">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]">
                  <Calendar className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-2">Annual Revival</h3>
                <p className="text-gray-700 mb-4">
                  Join us for powerful preaching and spiritual renewal during our annual revival services.
                </p>
                <p className="text-sm text-gray-600">Coming Soon</p>
              </CardContent>
            </Card>

            <Card className="bg-[#fffbeb] border-2 border-[#800020]">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]">
                  <Calendar className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-2">Church Anniversary</h3>
                <p className="text-gray-700 mb-4">
                  Celebrate God's faithfulness as we commemorate another year of ministry and blessing.
                </p>
                <p className="text-sm text-gray-600">Annual Celebration</p>
              </CardContent>
            </Card>

            <Card className="bg-[#fffbeb] border-2 border-[#800020]">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]">
                  <Calendar className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-2">Youth Conferences</h3>
                <p className="text-gray-700 mb-4">
                  Special events designed to inspire and equip our young people for Christian living.
                </p>
                <p className="text-sm text-gray-600">Throughout the Year</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bulletin Download */}
      <section className="py-16 bg-[#800020] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Weekly Bulletin</h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-[#fef3c7] mb-8">
            Stay informed with our weekly bulletin featuring upcoming events, announcements, and prayer requests.
          </p>
          <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8860B] text-white px-8 font-semibold">
            <Download className="h-5 w-5 mr-2" />
            Download This Week's Bulletin
          </Button>
        </div>
      </section>
    </div>
  );
};
